
import React, { FunctionComponent, useState } from "react";
import getImageAspectRatio from "./helpers/getImageAspectRatio";
import getImageCrop from "./helpers/getImageCrop";
import imageData from "./helpers/imageData";
import styles from "./Image.module.scss";
import { ImageRequiredProps } from "./ImageProps";
import combineClasses from "helpers/combine-classes";
import setImageOptionsUmbraco from "./helpers/setImageOptionsUmbraco";
import setImageOptionsSrc from "./helpers/setImageOptionsSrc";

/**
 * @prop image/src (one required) - The image to be displayed. Either an Umbraco image object or a url to an image.
 * @prop alternativeText (required) - The alternative text for the image.
 * @prop crop (optional) - The crop to be used. If not provided, the original image will be used. If a crop is provided then the there should be no height prop provided.
 * @prop width (optional) - The width object for the image. Should be in the format {desktop: number, tablet: number, mobile: number}
 * @prop height (optional) - The height object for the image. Should be in the format {desktop: number, tablet: number, mobile: number}. If a crop is provided then the there should be no height prop provided.
 * @prop wrapperClass (optional) - The class to be applied to the wrapper div.
 * @prop imageClass (optional) - The class to be applied to the image.
 * @prop pictureClass (optional) - The class to be applied to the picture element.
 */

const Image: FunctionComponent<ImageRequiredProps> = (props): JSX.Element => {
    const { image, src, crop, wrapperClass, imageClass, pictureClass, alternativeText, width, height } = props;

    //TODO: Add loading state
    const [isLoaded, setIsLoaded] = useState<boolean>(false);

    const preferredCrop = crop ? crop : "Original";
    const imageCrop = image && getImageCrop(image?.umbracoFile?.crops, preferredCrop);

    const imageUrls = image ? setImageOptionsUmbraco(image.umbracoFile.src, imageCrop, width, height) : image.umbracoFile.src && width && height ? setImageOptionsSrc(image.umbracoFile.src, width, height) : [];

    const aspectRatio = image && getImageAspectRatio(imageCrop, image?.umbracoWidth, image?.umbracoHeight);
    const aspectRatioStyle =
        aspectRatio && aspectRatio.aspectRatioX && aspectRatio.aspectRatioY
            ? { aspectRatio: `${aspectRatio.aspectRatioX}/${aspectRatio.aspectRatioY}` }
            : width && height
            ? { aspectRatio: `${width.desktop}/${height.desktop}` }
            : {};

    const loadingSpeed = props.eager && props.eager === true ? "eager" : "lazy";

    return (
        <div
            className={combineClasses(styles.wrapper, wrapperClass)}
            style={aspectRatioStyle}
        >
            <div
                data-name="inner"
                className={styles.inner}
            >
                <picture className={combineClasses(styles.picture, pictureClass)}>
                    {imageUrls && imageUrls.length > 0 ? (
                        <>
                            {imageUrls.map((imageUrl: any, index: number) => {
                                return (
                                    <source
                                        key={index}
                                        srcSet={imageUrl}
                                        type={imageData[index].type}
                                        media={imageData[index].mediaSize}
                                        sizes={`${imageData[index].width}`}
                                    />
                                );
                            })}

                            <img
                                src={imageUrls[imageUrls.length - 1]}
                                alt={alternativeText}
                                className={combineClasses(styles.image, imageClass)}
                                loading={loadingSpeed}
                            />
                        </>
                    ) : null}
                </picture>
            </div>
        </div>
    );
};

export default Image;
